import type { ControlTowerViewFilters } from "../api/controlTower";
import type { TypedFolderScope } from "./folderScopes";
import type {
    ApiBounds,
    ConnectionHealthAgainst,
    ConnectionHealthCategory,
    TableConfig,
} from "./interfaces";
import type { NarrativeExportFormat } from "./narrativeExport";

// Query-key roots that also gate default IndexedDB persistence — see
// `DOCUMENT_AREA_QUERY_KEY_ROOTS` below. Defined once so a future rename of a
// root can't silently drift the persistence allowlist out of sync with the
// key it's meant to match.
const DOCUMENTS_ROOT = "documents";
const AI_DOCUMENTS_ROOT = "ai-documents";
const DOCUMENT_SUMMARY_ROOT = "documentSummary";
const FOLDERS_ROOT = "folders";
const DOCUMENTS_FOLDERED_ROOT = "documents-foldered";
const RFIS_ROOT = "rfis";
const CONVERSATION_ROOT = "conversation";
// Root of every connection-health key (PP-1109). The payloads are admin-only and the
// fleet one is cross-tenant, so `shouldPersistQuery` keeps the whole root out of
// IndexedDB by matching this first segment.
const CONNECTION_HEALTH_ROOT = "connection-health";
// Every revision-narrative comparison list hangs off this root, Home's included, so a
// narrative change that invalidates the root refreshes Home's last three too.
const REVISION_COMPARISONS_ROOT = "revision-comparisons";

/** Key segment of the section-group list that carries no signed file links. */
export const SECTION_GROUPS_WITHOUT_FILE_URLS = "without-file-urls";

export const PrimeQueryKeys = {
    /** Root shared by every admin-tenancy query key -- staff/company-admin-only
     *  surface; used by shouldPersistQuery to keep the whole company/project
     *  administration tree out of the per-org IndexedDB store. */
    adminTenancyRoot: ["admin-tenancy"] as const,
    adminTenancy: (identity: string) => ["admin-tenancy", identity] as const,
    adminTenancyTree: (identity: string, params: object) =>
        ["admin-tenancy", identity, "tree", params] as const,
    /** Status watch for the operations a loaded tree page is showing as
     *  running. Separate from the tree itself: React Query refetches EVERY
     *  loaded page of an infinite query, so polling the tree scales with how
     *  far the admin has paged, while this stays one query per company. */
    adminTenancyTreeWatch: (identity: string, companyId: string, operationIds: string[]) =>
        ["admin-tenancy", identity, "tree-watch", companyId, operationIds] as const,
    adminTenancyOperations: (identity: string) =>
        ["admin-tenancy", identity, "operations"] as const,
    adminTenancyOperation: (identity: string, id: string) =>
        ["admin-tenancy", identity, "operation", id] as const,
    // PP-827: fallback facts for refusal codes whose 409 body lacks the full
    // server-built Refusal. Fetched once per identity and cached indefinitely.
    adminTenancyRefusalCatalog: (identity: string) =>
        ["admin-tenancy", identity, "refusal-catalog"] as const,
    // PP-827 setup: Overview/Storage/Setup & health/Legacy blockers tabs.
    adminTenancyHealth: (identity: string) => ["admin-tenancy", identity, "health"] as const,
    adminTenancyStorageTargets: (identity: string) =>
        ["admin-tenancy", identity, "storage-targets"] as const,
    adminTenancyLegacyBlockers: (identity: string, organizationId: string) =>
        ["admin-tenancy", identity, "legacy-blockers", organizationId] as const,
    adminTenancySchemaReview: (identity: string, projectId: string) =>
        ["admin-tenancy", identity, "schema-review", projectId] as const,
    /** Prefix of every open-thread key; the Questions screen registers it for
     *  persistence (`screens/Questions/register.ts`), so it is locked to the builder.
     *  An admin-chat thread is not a Questions thread: it lives under its own root
     *  below, so the durable tenant cache never holds an `admin_edit` conversation. */
    conversationRoot: [CONVERSATION_ROOT] as const,
    conversation: (id: string) => [CONVERSATION_ROOT, id],
    documentById: (id: string) => ["document", id],
    markupComments: (
        fileId: string,
        page: number,
        revisionId: string | null,
        sectionGroupId?: string | null,
    ) => ["markup-comments", fileId, page, revisionId, ...(sectionGroupId ? [sectionGroupId] : [])],
    documentControlRoot: ["documentControl"],
    documentControlIssues: (documentId: string) => ["documentControl", documentId, "issues"],
    // `documentId` omitted keys the project-wide list (the Documents sidebar's
    // Transmittals workspace); passed, it keys one register's document.
    documentControlTransmittals: (documentId?: string) => [
        "documentControl",
        documentId ?? "all",
        "transmittals",
    ],
    // Deliberately NOT under `documentControlRoot`: this is the sidebar badge,
    // and it only changes when a transmittal is created. Under that root every
    // invalidateRevisionQueries call and every inline sheet metadata edit
    // refetched it.
    documentControlTransmittalsCount: () => ["documentControlTransmittalsCount"],
    documentControlTransmittal: (id: string) => ["documentControl", "transmittal", id],
    documentControlTransmittalPreview: (documentId: string, ids: readonly string[]) => [
        "documentControl",
        documentId,
        "transmittalPreview",
        ...ids,
    ],
    documentControlActivityRoot: (documentId: string) => [
        "documentControl",
        documentId,
        "activity",
    ],
    documentControlActivity: (documentId: string, kind?: string) => [
        "documentControl",
        documentId,
        "activity",
        kind ?? "all",
    ],
    documentControlArchived: (documentId: string) => ["documentControl", documentId, "archived"],
    documentControlGeneration: (documentId: string) => [
        "documentControl",
        documentId,
        "generation",
    ],
    documentControlCorrection: (documentId: string, operationId: string) => [
        "documentControl",
        documentId,
        "correction",
        operationId,
    ],
    replacementTargets: (revisionId: string) => [
        "documentControl",
        "replacementTargets",
        revisionId,
    ],
    specOutline: (revisionId: string) => ["documentControl", "specOutline", revisionId],
    /** A spec upload's sections as review rows (api/specOutline.ts `getSections`). */
    specSections: (revisionId: string) => ["documentControl", "specSections", revisionId],
    /** When this tab last saved the spec outline (`useSpecOutlineSaved`): polling restarts its back-off there. */
    specOutlineSavedAt: (revisionId: string) => ["documentControl", "specOutlineSavedAt", revisionId],
    /** A version set's type and reissue review (api/reissue.ts). Under
     *  `documentControlRoot`, so every revision lifecycle invalidation refreshes it. */
    reissueState: (revisionId: string) => ["documentControl", "reissue", revisionId],
    /** What changing a published set's type would do; read fresh per dialog open. */
    setTypePreview: (revisionId: string, setType: string) => [
        "documentControl",
        "setTypePreview",
        revisionId,
        setType,
    ],
    sharedConversation: (shareId: string) => ["shared-conversation", shareId],
    /** The per-document admin-chat conversation (useAdminChatConversation). */
    adminChatConversation: (conversationId: string) =>
        ["admin-chat-conversation", conversationId] as const,
    /** Admin-chat change reports for one conversation (AdminChatApi.listChanges). */
    adminChatChanges: (conversationId: string) => ["admin-chat-changes", conversationId] as const,
    regionCoverage: (fileId: string, bounds: ApiBounds, sameFloorOnly: boolean) => [
        "region-coverage",
        fileId,
        bounds.page,
        bounds.xMin,
        bounds.yMin,
        bounds.xMax,
        bounds.yMax,
        sameFloorOnly,
    ],
    allConversations: "conversations",
    createConversation: "createConversation",
    showEmbeddingTextCookie: "prime_show_embedding_text",

    file: (id: string) => ["file", id],
    pageDimensions: (fileId: string) => ["page-dimensions", fileId],
    overlayTransforms: (
        baseSheetFileId: string,
        overlaySheetFileId: string,
        baseSectionGroupId: string | null,
        overlaySectionGroupId: string | null,
        tripletTags: string,
        baseDetailUuid: string | null,
        overlayDetailUuid: string | null,
    ) =>
        [
            "overlay-transforms",
            baseSheetFileId,
            overlaySheetFileId,
            baseSectionGroupId,
            overlaySectionGroupId,
            tripletTags,
            baseDetailUuid,
            overlayDetailUuid,
        ] as const,
    /**
     * The one sheet-bootstrap entry (`GET /file/{id}/sheet_bootstrap`: elements, dimensions and the
     * revision delta). Every input that changes the response is in the key, delta inputs and the shop
     * scope included, so two scopes of one file never share an entry. Elements, dimensions and the
     * delta are `select` projections of it. It starts with `fileElements`' key, so an invalidation by
     * that prefix reaches it.
     */
    sheetBootstrap: (p: {
        fileId: string;
        page: number;
        includeUnconnected?: boolean;
        revisionId?: string;
        scopeSectionGroupId?: string;
        sectionGroupId?: string;
    }) => [
        "file",
        p.fileId,
        "elements",
        p.page,
        p.includeUnconnected ?? false,
        p.revisionId ?? null,
        p.scopeSectionGroupId ?? null,
        p.sectionGroupId ?? null,
    ],
    /** The prefix of every sheet-bootstrap entry of one page, projection and unconnected flag. */
    fileElements: (
        id: string,
        page: number,
        includeUnconnected: boolean = false,
        revisionId?: string,
    ) => ["file", id, "elements", page, includeUnconnected, revisionId ?? null],
    /** Prefix for every file-elements variant on one page. */
    fileElementsPage: (id: string, page: number) => ["file", id, "elements", page] as const,
    pageTables: (id: string, page: number, sectionGroupId?: string) => [
        "file",
        id,
        "page_tables",
        page,
        sectionGroupId ?? null,
    ],
    scheduleActivities: (fileId: string) => ["file", fileId, "schedule_activities"],
    scheduleActivityLocation: (sectionUuid: string) =>
        ["schedule", "activity", sectionUuid, "location"] as const,
    scheduleActive: ["schedule", "active"] as const,
    scheduleRevisions: ["schedule", "revisions"] as const,
    scheduleUploadStatus: (fileId: string, jobId?: number) =>
        ["schedule", "upload-status", fileId, jobId ?? null] as const,

    workflowProblems: ["workflow", "problems"],
    issueGroupMembers: (issueId: string) => ["issue", "group-members", issueId],
    /** Prefix over every group's member list — one scan patches or invalidates them all. */
    issueGroupMembersAll: ["issue", "group-members"],
    workflowProblemsForNode: (canReview: boolean, nodeId: string, viewingAsCustomer = false) =>
        [
            ...PrimeQueryKeys.workflowProblemsList(canReview, viewingAsCustomer),
            "node",
            nodeId,
        ] as const,
    /** The issue list the page is showing: a reviewer's, or the customer view's own entry. */
    workflowProblemsList: (canReview: boolean, viewingAsCustomer: boolean) =>
        [
            PrimeQueryKeys.workflowProblems,
            viewingAsCustomer ? "view-as-customer" : canReview,
        ] as const,
    // Kept outside the workflowProblems family because its cached value is an
    // object, while delete/undelete optimistically map every issue-list entry
    // under that family.
    workflowIssueFormDefaults: ["workflow", "issue-form-defaults"],
    // Also kept outside the workflowProblems family: its cached value is an object, not an issue list.
    constructabilityUnpublishedChanges: ["workflow", "unpublished-changes"] as const,
    /** Home's AI tools: the Constructability Review row's open and high counts
     *  (`GET /home/constructability-counts`). Outside the workflowProblems family: its
     *  value is an object, not an issue list. */
    homeConstructabilityCounts: ["home", "constructability-counts"] as const,
    /** Home's Starred and Recent: each of the device's ids resolved to its current card, or
     *  to null when the viewer has none (`POST /home/sheets`, batched), one entry per id. */
    homeSheetsRoot: ["home", "sheets"] as const,
    homeSheet: (id: string) => ["home", "sheets", id] as const,
    /** The member's own collections of drawing sheets (`GET /collections`): one list, every
     *  collection's current sheet ids. The store is already scoped per org, API and member. */
    myCollections: ["my-collections"] as const,
    preconRuns: ["precon-runs"] as const,
    /** Latest completed precon run summary per Drawing document.
     *  Role-segmented like clashAlertsForRole: the endpoint returns the full run
     *  summary (held finding counts, internal notes) to admins and a
     *  published-only tri-state to everyone else, and admin status can change
     *  without a QueryClient swap; one shared entry could serve the opposite
     *  role's payload. Invalidate with the `preconRuns` prefix to hit both. */
    preconRunsLatestForRole: (isAdmin: boolean) =>
        ["precon-runs", "latest", isAdmin ? "admin" : "member"] as const,
    /** The precon check registry (category + names per check). Static server
     *  metadata, identical for every role and every tenant — deliberately NOT
     *  under the `preconRuns` prefix, so invalidating a project's runs does not
     *  refetch it. */
    preconChecks: ["precon-checks"] as const,
    fixtureClashGeometry: (documentId: string, fileId: string) =>
        ["precon-runs", "fixture-geometry", documentId, fileId] as const,
    /** A rollup finding's paged members (infinite query). Under the `preconRuns`
     *  prefix: a re-run replaces the finding rows, and the members with them.
     *  A re-run can also refresh a row in place: the run that wrote the row
     *  (`ApiConstructabilityIssue.runId`) and its member count are in the key
     *  so the cached pages of the old run are left behind rather than extended
     *  with the new run's. */
    preconIssueMembers: (issueId: string, runId: string | null, childrenTotal: number | null) =>
        ["precon-runs", "issue-members", issueId, runId, childrenTotal] as const,
    workflowCurrentFile: ["workflow", "currentFile"],
    issueReviewEventsAll: ["issue-review-events"] as const,
    workflowStudioCatalog: ["workflow-studio", "catalog"] as const,
    workflowStudioWorkflows: ["workflow-studio", "workflows"] as const,
    workflowStudioPublishedTools: ["workflow-studio", "published-tools"] as const,
    /** Every published tool page's key starts with this; publishing or
     *  deleting a tool marks them all stale. */
    workflowStudioPublishedToolPages: ["workflow-studio", "published-tool"] as const,
    workflowStudioPublishedTool: (slug: string) =>
        ["workflow-studio", "published-tool", slug] as const,
    /** Every run's key starts with this; the run page sets their cache lifetime by it. */
    workflowStudioRuns: ["workflow-studio", "run"] as const,
    workflowStudioRun: (runId: string) => ["workflow-studio", "run", runId] as const,
    workflowStudioToolRuns: (slug: string) => ["workflow-studio", "tool-runs", slug] as const,
    workflowStudioToolDecisions: (slug: string) =>
        ["workflow-studio", "tool-decisions", slug] as const,
    issueReviewEvents: (issueId: string) => ["issue-review-events", issueId] as const,
    fragmentNode: (id: string) => ["fragment", id],
    relatedFragmentNodes: (id: string, fragmentUuid: string, variant: string) => [
        "file",
        id,
        "related-fragments",
        fragmentUuid,
        variant,
    ],
    sectionDetails: (sectionId: string) => ["sectionDetails", sectionId] as const,
    sectionContentStatuses: (sectionIds: string[]) =>
        ["sectionContentStatuses", ...[...sectionIds].sort()] as const,
    sectionReferences: (sectionUuid: string) => ["section", sectionUuid, "references"],
    /** Detector candidate row-sets (with verdicts) for one sheet's section group. */
    detectorCandidates: (sectionGroupId: string) =>
        ["detector", "candidates", sectionGroupId] as const,
    sectionReferenceGroupsInfinite: (sectionUuid: string) => [
        "section",
        sectionUuid,
        "reference-groups-infinite",
    ],
    tableRows: (sectionUuid: string) => ["section", sectionUuid, "children"],
    /** Detail chips of a sheet destination (the sheet root's keyed DrawingDetails),
     *  in the tableRows shape — see getSheetDetailKeys. */
    sheetDetailKeys: (sheetSectionUuid: string) => ["section", sheetSectionUuid, "detail-keys"],
    /** Matches any tableRows / sheetDetailKeys key regardless of section — for
     *  predicate-based invalidation of every row-key overlay after a cross-table
     *  mutation. Co-located with the factories so a key reshape can't detach the
     *  matcher. */
    isTableRowsKey: (queryKey: unknown): boolean =>
        Array.isArray(queryKey) &&
        queryKey[0] === "section" &&
        (queryKey[2] === "children" || queryKey[2] === "detail-keys"),
    /** Live "Extracted Keys" preview (previewTableKeys) — table-edit dialog and
     *  the component inspector's read-only key list. Omit tableConfig to get
     *  the invalidation prefix for one section. */
    previewTableKeys: (sectionId: string | null | undefined, tableConfig?: TableConfig | null) =>
        tableConfig === undefined
            ? ["previewTableKeys", sectionId]
            : ["previewTableKeys", sectionId, tableConfig],
    /** Disconnect Table dialog's filter-picker preview (getDisconnectTablePreview). */
    disconnectTablePreview: (sectionId: string | null) =>
        ["disconnectTablePreview", sectionId] as const,
    allFiles: "files",
    documents: (type?: string) => (type ? [DOCUMENTS_ROOT, type] : [DOCUMENTS_ROOT]),
    documentSummary: DOCUMENT_SUMMARY_ROOT,

    // Document Management v2 (Documents category / folder tree). `folders` is Additional docs'
    // tree and the prefix of every other top-level folder's (`foldersIn`): invalidating it
    // covers them all.
    folders: [FOLDERS_ROOT] as const,
    foldersIn: (scope: TypedFolderScope | "all") => [FOLDERS_ROOT, scope] as const,
    // Root prefix — invalidate this to cover every folderId variant below.
    documentsFolderedRoot: [DOCUMENTS_FOLDERED_ROOT] as const,
    /** A folder's files, or a top-level folder's outside any folder: Additional docs' without
     *  `scope`, a type's with it. */
    documentsFoldered: (folderId?: string, scope?: TypedFolderScope) =>
        scope
            ? ([
                  ...PrimeQueryKeys.documentsFolderedRoot,
                  `scope:${scope}`,
                  folderId ?? "all",
              ] as const)
            : ([...PrimeQueryKeys.documentsFolderedRoot, folderId ?? "all"] as const),
    projectSession: (orgSlug: string) => ["project-session", orgSlug] as const,
    overviewStartup: ["overview-startup", 1] as const,
    findIndex: ["findInProject", "index"] as const,
    findDetails: ["findInProject", "details", 1] as const,
    projectDocuments: (orgSlug: string, documentType?: string) =>
        [...PrimeQueryKeys.documents(documentType), orgSlug] as const,
    projectSectionGroupsByType: (
        orgSlug: string,
        documentType: "drawing" | "csi" | "shop_drawing" | "azdot" | "txdot",
    ) => ["documentSectionGroups", documentType, orgSlug] as const,
    projectFolderedDocuments: (orgSlug: string, folderId?: string) =>
        [...PrimeQueryKeys.documentsFoldered(folderId), orgSlug] as const,
    projectRfis: (orgSlug: string, offset: number, limit: number) =>
        [...PrimeQueryKeys.rfis(), orgSlug, offset, limit] as const,
    projectScheduleActive: (orgSlug: string) =>
        [...PrimeQueryKeys.scheduleActive, orgSlug] as const,
    projectScheduleActivities: (orgSlug: string, fileId: string) =>
        [...PrimeQueryKeys.scheduleActivities(fileId), orgSlug] as const,
    folderUploadIngestionStatus: (fileIds: string[]) =>
        ["folder-upload-ingestion-status", fileIds] as const,
    documentIngestionDetail: (fileId: string) => ["document-ingestion-detail", fileId] as const,
    folderImportStatus: (importIds: string[]) => ["folder-import-status", importIds] as const,
    fileSectionGroups: (id: string) => ["file", id, "section_groups"],
    searchSheets: (documentId: string, query: string, areaId?: string) => [
        "search-sheets",
        documentId,
        query,
        areaId ?? null,
    ],
    streamingSearch: (documentId: string, query: string) => ["streaming-search", documentId, query],
    // Area twins share (documentId, groupCode) — the section-group uuid keeps
    // their caches apart. The shorter form remains a prefix of the scoped form,
    // so invalidating without a sectionGroupId still hits every scoped entry.
    sheetRevisions: (documentId: string, groupCode: string, sectionGroupId?: string) =>
        sectionGroupId
            ? ["sheet-revisions", documentId, groupCode, sectionGroupId]
            : ["sheet-revisions", documentId, groupCode],
    allSheets: "allSheets",
    uploadedFiles: "uploadedFiles",

    adminOverview: ["admin", "overview"],
    adminMembers: ["admin", "members"],
    adminOrgAccess: ["admin", "org-access"],
    /** Staff-only recorded Marvin runs carry customer questions and answers, tenant slugs and
     *  stored sheet crops: the whole prefix stays out of the per-org browser store. */
    marvinEvalRunsRoot: ["admin", "marvin-eval-runs"] as const,
    /** Staff-only Marvin settings: prompts and tenant instructions are customer-specific. */
    marvinSettingsRoot: ["admin", "marvin-settings"] as const,
    marvinSettings: (tenant: string | null) =>
        ["admin", "marvin-settings", "settings", tenant ?? "global"] as const,
    /** A Marvin artifact's body, in the form a widget shows it (`useArtifactContent`). */
    marvinArtifact: (artifactId: string, shareId: string | null, form: "html" | "image") =>
        ["marvin-artifact", artifactId, shareId, form] as const,
    marvinProjectSettings: (organizationId: string | undefined) =>
        ["marvin-project-settings", organizationId ?? ""] as const,
    /** The iOS app's link and this person's Get the app banner (`api/apps.ts`). */
    apps: (organizationId: string | undefined) => ["apps", organizationId ?? ""] as const,
    /** The Get the app banners closed in this tab (`useGetTheAppBanner`); never fetched. */
    appsClosed: (organizationId: string | undefined) =>
        ["apps-closed", organizationId ?? ""] as const,
    marvinSettingsTenants: ["admin", "marvin-settings", "tenants"] as const,
    marvinSettingsTenant: (slug: string) => ["admin", "marvin-settings", "tenant", slug] as const,
    marvinSettingsPrompts: ["admin", "marvin-settings", "prompts"] as const,
    marvinSettingsPrompt: (piece: string) => ["admin", "marvin-settings", "prompt", piece] as const,
    marvinSettingsInstructions: (slug: string) =>
        ["admin", "marvin-settings", "instructions", slug] as const,
    marvinSettingsHistory: ["admin", "marvin-settings", "history"] as const,
    marvinEvalRuns: ["admin", "marvin-eval-runs", "list"] as const,
    marvinEvalRun: (runId: string) => ["admin", "marvin-eval-runs", "run", runId] as const,
    marvinEvalQuestion: (runId: string, questionId: string) =>
        ["admin", "marvin-eval-runs", "question", runId, questionId] as const,
    marvinEvalVerdicts: (base: string, candidate: string) =>
        ["admin", "marvin-eval-runs", "verdicts", base, candidate] as const,
    preconOntologyRoot: ["admin", "precon-ontology"] as const,
    preconOntology: (route: string, params: Record<string, string | number>) =>
        ["admin", "precon-ontology", route, params] as const,
    mirandaRunsRoot: ["admin", "miranda-runs"] as const,
    mirandaRuns: ["admin", "miranda-runs", "list"] as const,
    mirandaRun: (runId: string) => ["admin", "miranda-runs", "run", runId] as const,
    mirandaRunSheets: (runId: string, discipline: string, state?: string) =>
        ["admin", "miranda-runs", "sheets", runId, discipline, state ?? null] as const,
    mirandaRunReview: (runId: string) => ["admin", "miranda-runs", "review", runId] as const,
    mirandaRunReport: (runId: string) => ["admin", "miranda-runs", "report", runId] as const,
    mirandaRunReviewRows: (
        runId: string,
        section: string,
        destination?: string,
        severity?: string,
    ) =>
        [
            "admin",
            "miranda-runs",
            "review-rows",
            runId,
            section,
            destination ?? null,
            severity ?? null,
        ] as const,
    /** Prefix shared by every revision-dash query key — staff-only surface;
     *  used by shouldPersistQuery to keep the whole surface out of the
     *  per-org IndexedDB store, not just the fleet key. */
    revisionDashRoot: ["admin", "revision-dash"] as const,
    revisionDashFleet: ["admin", "revision-dash", "fleet"] as const,
    revisionDashTenant: (slug: string) => ["admin", "revision-dash", "tenant", slug] as const,
    revisionDashTenantLive: (slug: string) =>
        ["admin", "revision-dash", "tenant-live", slug] as const,
    revisionDashAnchorPreview: (
        slug: string,
        integrationType?: string,
        projectId?: string,
        category?: string,
        asOf?: string,
    ) =>
        [
            "admin",
            "revision-dash",
            "anchor-preview",
            slug,
            integrationType,
            projectId,
            category,
            asOf,
        ] as const,
    revisionDashCatchUp: (
        slug: string,
        integrationType?: string,
        projectId?: string,
        category?: string,
    ) =>
        ["admin", "revision-dash", "catch-up", slug, integrationType, projectId, category] as const,
    revisionDashBacklog: (slug: string, integrationType?: string, projectId?: string) =>
        ["admin", "revision-dash", "backlog", slug, integrationType, projectId] as const,
    revisionDashSyncStatus: (slug: string, integrationType?: string, projectId?: string) =>
        ["admin", "revision-dash", "sync-status", slug, integrationType, projectId] as const,
    projectBootstrap: (slug: string, integrationType?: string, projectId?: string) =>
        ["admin", "revision-dash", "project-bootstrap", slug, integrationType, projectId] as const,
    revisionDashProjectProcessingSets: (slug: string) =>
        ["admin", "revision-dash", "project-processing-sets", slug] as const,
    revisionDashAutoImport: (slug: string) =>
        ["admin", "revision-dash", "auto-import", slug] as const,
    revisionDashDigest: ["admin", "revision-dash", "digest"] as const,
    revisionDashProcoreProjects: (slug: string) =>
        ["admin", "revision-dash", "procore-projects", slug] as const,
    revisionDashAccProjects: (slug: string) =>
        ["admin", "revision-dash", "acc-projects", slug] as const,
    /** Prefix shared by every Admin console query (PP-1231): staff-only, platform-wide data, so
     *  the whole prefix stays out of the per-org store (`shouldPersistQuery`). A console write
     *  refreshes only what it touched (`useRefreshConsole`): a company's or project's key is the
     *  prefix of its tabs' keys, and the `*All` keys are the prefix of every one of them. */
    adminConsoleRoot: ["admin", "console"] as const,
    adminConsoleCompanies: ["admin", "console", "companies"] as const,
    adminConsoleCompanyAll: ["admin", "console", "company"] as const,
    adminConsoleCompany: (slug: string) => ["admin", "console", "company", slug] as const,
    adminConsoleCompanyPeople: (slug: string) =>
        ["admin", "console", "company", slug, "people"] as const,
    adminConsoleCompanyAdmins: (slug: string) =>
        ["admin", "console", "company", slug, "admins"] as const,
    adminConsoleCompanyAdminPreview: (slug: string, email: string) =>
        ["admin", "console", "company", slug, "admins", "preview", email] as const,
    adminConsoleCompanySignIn: (slug: string) =>
        ["admin", "console", "company", slug, "sign-in"] as const,
    adminConsoleProjects: ["admin", "console", "projects"] as const,
    adminConsoleProjectAll: ["admin", "console", "project"] as const,
    adminConsoleProject: (slug: string) => ["admin", "console", "project", slug] as const,
    adminConsoleProjectMembers: (slug: string) =>
        ["admin", "console", "project", slug, "members"] as const,
    adminConsoleProjectFlags: (slug: string) =>
        ["admin", "console", "project", slug, "flags"] as const,
    adminConsoleProjectSignIn: (slug: string) =>
        ["admin", "console", "project", slug, "sign-in"] as const,
    adminConsoleCreation: (slug: string) => ["admin", "console", "creation", slug] as const,
    adminConsoleAddressCheck: (company: string, project: string) =>
        ["admin", "console", "address-check", company, project] as const,
    adminConsolePeopleAll: ["admin", "console", "people"] as const,
    adminConsolePeople: (q: string) => ["admin", "console", "people", q] as const,
    adminConsolePersonAll: ["admin", "console", "person"] as const,
    adminConsolePerson: (email: string) => ["admin", "console", "person", email] as const,
    adminConsoleStaff: ["admin", "console", "staff"] as const,
    adminConsoleActivityAll: ["admin", "console", "activity"] as const,
    adminConsoleActivity: (filter: unknown) => ["admin", "console", "activity", filter] as const,
    adminConsolePipelineAll: ["admin", "console", "pipeline"] as const,
    adminConsolePipelineRefresh: ["admin", "console", "pipeline", "refresh"] as const,
    adminConsolePipelineSummaryAll: ["admin", "console", "pipeline", "summary"] as const,
    adminConsolePipelineSummary: (days: number) =>
        ["admin", "console", "pipeline", "summary", days] as const,
    adminConsolePipelineStepsAll: ["admin", "console", "pipeline", "steps"] as const,
    adminConsolePipelineSteps: (days: number) =>
        ["admin", "console", "pipeline", "steps", days] as const,
    adminConsolePipelineRunsAll: ["admin", "console", "pipeline", "runs"] as const,
    adminConsolePipelineRuns: (filter: unknown) =>
        ["admin", "console", "pipeline", "runs", filter] as const,
    adminConsolePipelineRevision: (tenant: string, revisionId: string) =>
        ["admin", "console", "pipeline", "revision", tenant, revisionId] as const,
    adminConsolePipelineRun: (tenant: string, runId: string) =>
        ["admin", "console", "pipeline", "run", tenant, runId] as const,
    adminConsolePipelineWorkers: (app: string | null) =>
        ["admin", "console", "pipeline", "workers", app] as const,
    engagementReport: (days: number, weeks: number, includeInternal: boolean) =>
        ["admin", "engagement-report", days, weeks, includeInternal] as const,
    /** Staff-only, cross-tenant analytics prefix. Keep this builder rooted here
     *  so the IndexedDB persistence exclusion cannot drift from its key shape. */
    fleetEngagementReportRoot: ["admin", "fleet-engagement-report"] as const,
    fleetEngagementReport: (days: number) =>
        [...PrimeQueryKeys.fleetEngagementReportRoot, days] as const,
    adminConsoleEngagementAll: ["admin", "console", "engagement"] as const,
    adminConsoleEngagement: (query: unknown) => ["admin", "console", "engagement", query] as const,
    usageAll: ["usage"] as const,
    usageTree: (periods: unknown) => ["usage", "tree", periods] as const,
    usage: (view: string, query: unknown) => ["usage", view, query] as const,
    adminConsoleEmailsAll: ["admin", "console", "emails"] as const,
    adminConsoleEmails: ["admin", "console", "emails", "list"] as const,
    adminConsoleEmail: (id: string) => ["admin", "console", "emails", "email", id] as const,
    adminConsoleEmailAudience: (id: string) =>
        ["admin", "console", "emails", "audience", id] as const,
    adminConsoleEmailResults: (id: string) =>
        ["admin", "console", "emails", "results", id] as const,
    // Keyed by member: a member switch inside the same org-scoped
    // QueryClient must never serve the previous caller's cached roles.
    myStaffRoles: (memberId: string) => ["me", "staff-roles", memberId] as const,
    adminParseDebugTree: (documentId: string) =>
        ["admin", "parse-debug", "tree", documentId] as const,
    validationRun: (runId: string) => ["admin", "validation-run", runId] as const,
    llmLogSummary: (filters: unknown) => ["admin", "llm-log", "summary", filters] as const,
    llmLogCalls: (filters: unknown) => ["admin", "llm-log", "calls", filters] as const,
    llmLogCall: (callId: number) => ["admin", "llm-log", "call", callId] as const,
    /** Invalidation prefix for all externalFileRevisionGroups query keys. */
    externalFileRevisionGroupsPrefix: ["admin", "external-file-revision-groups"] as const,
    externalFileRevisionGroups: (
        integrationType?: string,
        projectId?: string,
        category?: string,
        areaId?: string,
        status?: string,
    ) =>
        [
            "admin",
            "external-file-revision-groups",
            integrationType,
            projectId,
            category,
            areaId,
            status,
        ] as const,
    externalFileLocalDocuments: ["admin", "external-file-local-documents"] as const,
    externalFileLocalSectionGroups: (documentIds: string[]) =>
        ["admin", "external-file-local-section-groups", ...[...documentIds].sort()] as const,
    externalFileUrl: (id: string | undefined) => ["admin", "external-file-url", id] as const,
    /** Call with no args to get the invalidation prefix; call with all params for a query key. */
    externalFileVerificationFiles: (...params: (string | number | undefined)[]) => [
        "admin",
        "external-file-verification-files",
        ...params,
    ],

    // Externally managed docs — Setup/Revisions plus the surviving Reports
    // drill-downs. `category` is appended only when passed (rather than always,
    // as `undefined`) so 2-arg drawing-only invalidation call sites elsewhere
    // (ExternalFilesAdminPage, MappingModelPanel) keep prefix-matching every
    // category variant instead of missing the Specifications-scoped cache entry.
    externalDocsAllRevisionGroups: (
        integrationType?: string,
        projectId?: string,
        category?: string,
    ) =>
        [
            "admin",
            "external-docs",
            "all-revision-groups",
            integrationType,
            projectId,
            ...(category ? [category] : []),
        ] as const,
    externalDocsLocalRevisions: (category?: string) =>
        ["admin", "external-docs", "local-revisions", ...(category ? [category] : [])] as const,
    externalDocsBacklog: (integrationType?: string, projectId?: string, category?: string) =>
        [
            "admin",
            "external-docs",
            "backlog",
            integrationType,
            projectId,
            ...(category ? [category] : []),
        ] as const,
    externalDocsSpecComparison: (baseRevisionId?: string, targetRevisionId?: string) =>
        ["admin", "external-docs", "spec-comparison", baseRevisionId, targetRevisionId] as const,
    externalDocsSpecSectionSearch: (
        integrationType?: string,
        projectId?: string,
        search?: string,
    ) =>
        [
            "admin",
            "external-docs",
            "spec-section-search",
            integrationType,
            projectId,
            search,
        ] as const,
    externalDocsReportRuns: (
        integrationType?: string,
        projectId?: string,
        limit?: number,
        offset?: number,
    ) =>
        [
            "admin",
            "external-docs",
            "report-runs",
            integrationType,
            projectId,
            limit,
            offset,
        ] as const,
    externalDocsReportRunDetail: (jobId?: string, integrationType?: string, projectId?: string) =>
        ["admin", "external-docs", "report-run-detail", jobId, integrationType, projectId] as const,
    externalDocsReportRevisionDetail: (
        integrationType?: string,
        projectId?: string,
        category?: string,
        areaId?: string,
    ) =>
        [
            "admin",
            "external-docs",
            "report-revision-detail",
            integrationType,
            projectId,
            category,
            areaId,
        ] as const,
    externalDocsRevisionDetailFiles: (
        integrationType?: string,
        projectId?: string,
        revisionGroupKey?: string,
        fileCategory?: string,
    ) =>
        [
            "admin",
            "external-docs",
            "revision-detail-files",
            integrationType,
            projectId,
            revisionGroupKey,
            fileCategory,
        ] as const,
    externalDocsReportMissing: (
        integrationType?: string,
        projectId?: string,
        sense?: string,
        category?: string,
        areaId?: string,
        offset?: number,
    ) =>
        [
            "admin",
            "external-docs",
            "report-missing",
            integrationType,
            projectId,
            sense,
            category,
            areaId,
            offset,
        ] as const,
    externalDocsReportAreas: (integrationType?: string, projectId?: string) =>
        ["admin", "external-docs", "report-areas", integrationType, projectId] as const,
    externalDocsAreaMappings: (integrationType?: string, projectId?: string) =>
        ["admin", "external-docs", "area-mappings", integrationType, projectId] as const,
    externalDocsAreaModel: (integrationType?: string, projectId?: string) =>
        ["admin", "external-docs", "area-model", integrationType, projectId] as const,
    // Processing jobs and workflows
    jobs: ["jobs"] as const,
    incompleteWorkflows: ["workflows", "incomplete"] as const,
    recentWorkflows: ["workflows", "recent"] as const,
    workflowDag: (workflowId: string) => ["workflows", "dag", workflowId] as const,
    workflowStepNodes: (workflowId: string, step: string, status: string) =>
        ["workflows", "dag", workflowId, "steps", step, status] as const,
    queueJobs: ["jobs", "queue"] as const,
    /** JobsApi.getStepCatalog — flow + on-demand processing-step registry.
     *  Deliberately outside the ["jobs"] prefix: the registry is static per
     *  deploy, so job-state invalidations must not refetch it. */
    availableProcessingSteps: ["available-processing-steps"] as const,
    auditLogs: ["jobs", "audit-logs"] as const,
    jobsHealth: ["jobs", "health"] as const,
    jobsCatalog: ["jobs", "catalog"] as const,
    jobsSubjects: ["jobs", "subjects"] as const,
    frameworkRuns: ["jobs", "framework-runs"] as const,
    jobsPreparing: ["jobs", "preparing"] as const,
    jobsSubscriptions: ["jobs", "subscriptions"] as const,
    jobsEventKeys: ["jobs", "event-keys"] as const,
    frameworkRun: (runId: string) => ["jobs", "framework-runs", runId] as const,
    revisionPipelineStatus: (revisionId: string) =>
        ["jobs", "pipeline-status", revisionId] as const,
    documentJobs: (documentId: string) => ["jobs", "document", documentId] as const,
    documentJobsStatus: (documentId: string) => ["jobs", "document", documentId, "status"] as const,
    documentSheetsList: (documentId: string) => ["jobs", "document", documentId, "sheets"] as const,

    problemPairs: ["workflows", "problems"],

    showDebugInfoCookie: "showDebugInfoCookie",
    emptyConversationHistoryCookie: "noConversations",
    enableLocalCacheCookie: "prime_enable_local_cache",
    drawingSegmentationOverlayCookie: "prime_drawing_segmentation_overlay",
    fixtureClashGeometryCookie: "prime_fixture_clash_geometry",
    drawingEntitySegmentationCookie: "prime_drawing_entity_segmentation",
    drawingAnnotationInspectCookie: "prime_drawing_annotation_inspect",
    designV2BrowserCookie: "prime_design_v2_browser",
    buildingAssetsBrowserCookie: "prime_building_assets_browser",
    askRegionCookie: "prime_ask_region",
    clashAlertLayerCookie: "prime_clash_alert_layer",
    mirandaAdminChatEngineCookie: "prime_miranda_admin_chat_engine",

    rfiReviews: "rfiReviews",
    rfiReview: (id: string) => ["rfiReview", id],
    rfiDetail: (documentId: string) => ["rfi-detail", documentId],
    rfis: () => [RFIS_ROOT] as const,
    rfiSearch: (query: string) => ["rfi-search", query] as const,
    rfiLinks: (rfiId: string) => ["rfi-links", rfiId],
    rfiEvidenceImage: (linkId: string, version: number, orgSlug: string) =>
        ["rfi-evidence-image", orgSlug, linkId, version] as const,
    sheetRfiLinks: (documentId: string, groupCode: string) => [
        "sheet-rfi-links",
        documentId,
        groupCode,
    ],
    clashAlerts: ["clash-alerts"] as const,
    // Role-segmented: admin and non-admin payloads differ (review state merged
    // server-side vs published-only), and admin status can change without a
    // QueryClient swap — one shared entry could serve the opposite role's payload.
    // Invalidate with the `clashAlerts` prefix to hit both.
    clashAlertsForRole: (isAdmin: boolean) =>
        ["clash-alerts", isAdmin ? "admin" : "member"] as const,
    clashAlertOverlayPreviews: ["clash-alert-overlay-preview"] as const,
    clashAlertOverlayPreviewAlign: (baseFileId: string, overlayFileId: string) =>
        ["clash-alert-overlay-preview", "align", baseFileId, overlayFileId] as const,
    clashAlertOverlayPreviewImages: (cropsKey: string) =>
        ["clash-alert-overlay-preview", "images", cropsKey] as const,
    rfiReviewQueue: () => ["rfi-review-queue"] as const,
    submittalDetail: (documentId: string) => ["submittal-detail", documentId],
    submittalSyncStatus: () => ["submittal-sync-status"] as const,
    integrations: "integrations",
    // Role-segmented like clashAlertsForRole: GET /integrations returns ACC-only
    // rows for non-admins, and admin status can change without a QueryClient swap;
    // one shared entry could serve the opposite role's payload. Invalidate with
    // the `integrations` prefix to hit both.
    integrationsForRole: (isAdmin: boolean) =>
        ["integrations", isAdmin ? "admin" : "member"] as const,
    integrationStatus: ["integrations", "status"] as const,
    // Provider project-files folder listing (root or one folder's children) —
    // shared by the Configure Folders modal and the SharePoint library pickers.
    projectFilesFolders: (integrationId: string, folderId: string = "__root__") =>
        ["integrations", "project-files-folders", integrationId, folderId] as const,
    // Customer-facing "Data status" page (read-only, member-visible when feature-enabled).
    dataStatus: ["integrations", "data-status"] as const,
    dataStatusGroups: (integrationType: string, projectId: string, category: string) =>
        ["integrations", "data-status", "groups", integrationType, projectId, category] as const,
    dataStatusFiles: (
        integrationType: string,
        projectId: string,
        category: string,
        search: string,
        group: string,
        match: string,
        limit: number,
        offset: number,
    ) =>
        [
            "integrations",
            "data-status",
            "files",
            integrationType,
            projectId,
            category,
            search,
            group,
            match,
            limit,
            offset,
        ] as const,
    dataStatusSpecSections: (
        integrationType: string,
        projectId: string,
        search: string,
        limit: number,
        offset: number,
    ) =>
        [
            "integrations",
            "data-status",
            "spec-sections",
            integrationType,
            projectId,
            search,
            limit,
            offset,
        ] as const,
    dataStatusRecords: (
        integrationType: string,
        projectId: string,
        type: string,
        search: string,
        limit: number,
        offset: number,
    ) =>
        [
            "integrations",
            "data-status",
            "records",
            integrationType,
            projectId,
            type,
            search,
            limit,
            offset,
        ] as const,
    dataStatusSheetCompare: (efrId: string) =>
        ["integrations", "data-status", "compare", efrId] as const,
    // PP-1063 Provider Files (ACC Files / Procore Files in /documents)
    providerFileSections: ["provider-files", "sections"] as const,
    providerFileTree: (integrationId: string) => ["provider-files", integrationId, "tree"] as const,
    providerFileFolder: (integrationId: string, folderPath: string) =>
        ["provider-files", integrationId, "folder", folderPath] as const,
    providerFileIngestStatus: (integrationId: string) =>
        ["provider-files", integrationId, "ingest-status"] as const,
    providerFileUrl: (integrationId: string, revisionId: string) =>
        ["provider-files", integrationId, "file-url", revisionId] as const,
    comments: "comments",
    documentTypes: ["documentTypes"] as const,
    documentActivity: (documentId: string) => ["document", documentId, "activity"] as const,

    projectHealthStatus: ["project-health", "status"] as const,
    projectHealthFleet: ["project-health", "fleet"] as const,

    // Connection health station (api/connectionHealth.ts). Invalidate the root after a
    // capture or a kick-off change: both move the overview, every groups answer against
    // "kickoff", and the snapshot list at once.
    connectionHealthRoot: [CONNECTION_HEALTH_ROOT] as const,
    connectionHealthOverview: (days: number) => [CONNECTION_HEALTH_ROOT, "overview", days] as const,
    /** Prefix of that snapshot's groups and group-sheets keys. */
    connectionHealthSnapshot: (snapshotId: string) =>
        [CONNECTION_HEALTH_ROOT, "snapshot", snapshotId] as const,
    /** The groups of one (category, kind) of a snapshot; `against` and `limit` select
     *  different answers. Shares the `["groups", category, kind]` prefix with the group
     *  sheets below, so one (category, kind) invalidates together. */
    connectionHealthGroups: (
        snapshotId: string,
        category: ConnectionHealthCategory,
        kind: string,
        against: ConnectionHealthAgainst,
        limit: number,
    ) =>
        [
            CONNECTION_HEALTH_ROOT,
            "snapshot",
            snapshotId,
            "groups",
            category,
            kind,
            against,
            limit,
        ] as const,
    /** One page of a group's sheets, under the group's `key`. */
    connectionHealthGroupSheets: (
        snapshotId: string,
        category: ConnectionHealthCategory,
        kind: string,
        key: string,
        against: ConnectionHealthAgainst,
        page: number,
        pageSize: number,
    ) =>
        [
            CONNECTION_HEALTH_ROOT,
            "snapshot",
            snapshotId,
            "groups",
            category,
            kind,
            key,
            "sheets",
            against,
            page,
            pageSize,
        ] as const,
    /** One page of the lean history list; `null` = the newest page. `limit` is the page
     *  size asked for: two sizes from one cursor are different pages. */
    connectionHealthSnapshots: (beforeSeq: number | null, limit: number) =>
        [CONNECTION_HEALTH_ROOT, "snapshots", beforeSeq, limit] as const,
    connectionHealthFleet: [CONNECTION_HEALTH_ROOT, "fleet"] as const,

    controlTowerDashboards: ["control-tower", "dashboards"] as const,
    controlTowerFilterOptions: ["control-tower", "filter-options"] as const,
    controlTowerWidgetData: (
        dashboardId: string,
        filters: ControlTowerViewFilters,
        widgetIds: string[],
    ) =>
        [
            "control-tower",
            "widget-data",
            dashboardId,
            filters.from,
            filters.to,
            filters.granularity,
            filters.company ?? "all",
            filters.rawStatus ?? "",
            JSON.stringify(
                Object.entries(filters.custom ?? {}).sort(([left], [right]) =>
                    left.localeCompare(right),
                ),
            ),
            ...widgetIds,
        ] as const,
    controlTowerExecutiveSummary: (filters: ControlTowerViewFilters) =>
        [
            "control-tower",
            "executive-summary",
            filters.from,
            filters.to,
            filters.granularity,
            filters.company ?? "all",
            filters.rawStatus ?? "",
            JSON.stringify(
                Object.entries(filters.custom ?? {}).sort(([left], [right]) =>
                    left.localeCompare(right),
                ),
            ),
        ] as const,
    controlTowerReports: ["control-tower", "reports"] as const,
    controlTowerReport: (
        reportId: string,
        filters: ControlTowerViewFilters,
        flag: string | undefined,
    ) =>
        [
            "control-tower",
            "report",
            reportId,
            filters.from,
            filters.to,
            filters.granularity,
            filters.company ?? "all",
            filters.rawStatus ?? "",
            JSON.stringify(
                Object.entries(filters.custom ?? {}).sort(([left], [right]) =>
                    left.localeCompare(right),
                ),
            ),
            flag ?? "",
        ] as const,
    controlTowerQuery: (query: string) => ["control-tower", "query", query] as const,

    // Section group and fragment node queries
    sectionGroupSections: (sectionGroupId: string | null | undefined) =>
        ["sectionGroup", sectionGroupId, "sections"] as const,
    sheetNeighbors: (
        sectionGroupId?: string,
        fileId?: string,
        page?: number,
        sameArea?: boolean,
    ) => [
        "sheetNeighbors",
        sectionGroupId,
        sectionGroupId ? undefined : fileId,
        sectionGroupId ? undefined : page,
        sameArea ?? false,
    ],
    fragmentNodeFullText: (fragmentUuid: string) => ["fragmentNode", fragmentUuid, "fullText"],
    /** The sheet every node in one related-items list sits on, keyed by the
     *  joined node ids it covers. One list-level lookup — a row must never own
     *  a query of its own. */
    relatedNodeSheets: (nodeIdsKey: string) => ["relatedNodes", "sheets", nodeIdsKey] as const,

    specDrawingDocuments: () => ["spec-drawing-documents"],
    aliasDocuments: () => ["alias-documents"],
    documentsByType: (docType: "drawing" | "csi" | "shop_drawing") => [AI_DOCUMENTS_ROOT, docType],
    /** `withoutFileUrls` is the list shape that omits every row's signed `file.fileUrl`: its own
     *  entry (a reader of the register opens a clicked row from that URL), still under the doc
     *  type's prefix so every invalidation of the type reaches it. */
    sectionGroupsByDocType: (
        docType: "drawing" | "csi" | "shop_drawing",
        revisionId?: string,
        withoutFileUrls = false,
    ) => [
        "documentSectionGroups",
        docType,
        ...(revisionId ? ["revision", revisionId] : []),
        ...(withoutFileUrls ? [SECTION_GROUPS_WITHOUT_FILE_URLS] : []),
    ],
    specIngestionStatus: (fileId: string) => ["specIngestionStatus", fileId],
    documentDisciplines: (documentId: string, areaId?: string | null) => [
        "document",
        documentId,
        "disciplines",
        areaId ?? null,
    ],
    // Prefix across every areaId variant — for mutations that change a
    // sheet's effective discipline.
    documentDisciplinesRoot: (documentId: string) =>
        ["document", documentId, "disciplines"] as const,
    // Leads with its own token (unlike documentDisciplines' ["document", id, ...]) so
    // that BOTH one document and every document are prefix-invalidatable: deleting an
    // area moves its sheets to unassigned across the whole tenant, and no key with the
    // document id in the middle can express that sweep.
    documentAreaCounts: (documentId: string) => ["documentAreaCounts", documentId] as const,
    documentAreaCountsRoot: ["documentAreaCounts"] as const,
    documentDestinationIndex: (documentId: string | null | undefined) =>
        ["document", documentId, "destinationIndex"] as const,
    documentDestinationConnectionCounts: (documentId: string | null | undefined) =>
        ["document", documentId, "destinationConnectionCounts"] as const,
    /** Matches every document's destinationConnectionCounts key, for predicate invalidation. */
    isDestinationConnectionCountsKey: (queryKey: unknown): boolean =>
        Array.isArray(queryKey) &&
        queryKey[0] === "document" &&
        queryKey[2] === "destinationConnectionCounts",
    /** Shared prefix for every destination-dialog query on a document (summary +
     *  references) — invalidate this to refresh both after a mutation. Preview
     *  images live under a separate, content-addressed key (see
     *  `destinationDialogPreviews`) and are intentionally not covered by this prefix. */
    destinationDialogRoot: (documentId: string | null | undefined) =>
        ["destinationDialog", documentId] as const,
    destinationDialogSummary: (
        documentId: string | null | undefined,
        name: string,
        revisionId?: string | null,
        areaId?: string | null,
    ) =>
        [
            "destinationDialog",
            documentId,
            "summary",
            name,
            revisionId ?? null,
            areaId ?? null,
        ] as const,
    destinationDialogReferences: (
        documentId: string | null | undefined,
        name: string,
        selector: string, // "key:101A" | "tag:EF-1" | "shapes:3,7,-1"
        revisionId?: string | null,
        areaId?: string | null,
    ) =>
        [
            "destinationDialog",
            documentId,
            "references",
            name,
            selector,
            revisionId ?? null,
            areaId ?? null,
        ] as const,
    /** Document-wide free-text/stacked search results (references-shaped payload).
     *  Under the shared `destinationDialog` prefix so a mutation invalidation
     *  refreshes search hits too; `sheets` is the raw comma-separated glob string. */
    destinationDialogSearch: (
        documentId: string | null | undefined,
        query: string,
        sheets: string,
        revisionId?: string | null,
        areaId?: string | null,
    ) =>
        [
            "destinationDialog",
            documentId,
            "search",
            query,
            sheets,
            revisionId ?? null,
            areaId ?? null,
        ] as const,
    destinationDialogPreviews: (cropsKey: string) =>
        ["destinationDialog", "previews", cropsKey] as const,
    /**
     * Review-flagged taglists for a document, optionally narrowed to one
     * `ai_suggested_destination` (empty string = the document-wide aggregate that
     * powers the queue, the per-destination count map, and the picker badges).
     * Kept under the shared `destinationDialog` prefix so a destination mutation's
     * root invalidation also refreshes the review surfaces.
     */
    taglistReview: (
        documentId: string | null | undefined,
        suggestedDestination: string,
        revisionId?: string | null,
    ) =>
        [
            "destinationDialog",
            documentId,
            "taglistReview",
            suggestedDestination,
            revisionId ?? null,
        ] as const,
    /**
     * One flagged taglist's hydrated row (WITH tag crops) — the review listing
     * ships rows without crops, so the reviewed row fetches its own on
     * selection. Deliberately NOT under the `taglistReview` segment: the
     * review-mutation cache surgery matches that segment and edits LIST
     * payloads in place, which would corrupt a single-row entry.
     */
    taglistReviewCrops: (
        documentId: string | null | undefined,
        taglistId: string,
        revisionId?: string | null,
    ) =>
        [
            "destinationDialog",
            documentId,
            "taglistReviewCrops",
            taglistId,
            revisionId ?? null,
        ] as const,
    // Building-asset ontology (assets browser page)
    documentAssetIndex: (documentId: string) => ["document", documentId, "assets"] as const,
    documentAssetRoom: (documentId: string, roomKey: string) =>
        ["document", documentId, "assets", "rooms", roomKey] as const,
    documentSheets: (
        documentId: string,
        discipline?: string,
        pageSize?: number,
        areaId?: string | null,
    ) => ["document", documentId, "sheets", discipline, pageSize, areaId],

    // Areas (PP-580)
    areas: ["areas"] as const,
    areasProviderManaged: ["areas-provider-managed"] as const,
    areasImportPreview: (documentId: string) => ["areas-import-preview", documentId] as const,
    sheetTextMatches: (documentId: string, q: string) =>
        ["sheet-text-matches", documentId, q] as const,

    shapes: "shapes",
    shape: (id: number) => ["shape", id],
    textbox: (textboxId: string) => ["textbox", textboxId] as const,

    // Project settings
    projectSettingsAll: (orgSlug: string) => ["project-settings", "all", orgSlug],
    // Versioned below the existing invalidation prefix. An old unfiltered
    // response must never seed the narrower shell response from a warm cache.
    projectSettingsShell: (orgSlug: string) => ["project-settings", "all", orgSlug, "shell"],
    // Prefix of projectSettingsAll across every org — for invalidating the
    // batch dict a per-key settings cache hydrates from.
    projectSettingsAllRoot: ["project-settings", "all"] as const,
    projectSettingsFeatures: ["project-settings", "features"],
    // Tenant-scoped variant (embed auth can switch organizations in place, so the
    // identity rides in the key); base key when no organization id is resolvable.
    projectSettingsFeaturesForOrg: (organizationId?: string) =>
        organizationId
            ? [...PrimeQueryKeys.projectSettingsFeatures, organizationId]
            : PrimeQueryKeys.projectSettingsFeatures,
    // Unified GET /project-settings/feature-flags: registry + values + audit +
    // unregistered keys + env gates, one call — the Feature Flags admin page.
    projectSettingsFeatureFlags: ["project-settings", "feature-flags"],
    // Staff-only GET /ops/global-flags: platform-wide flags, one value for every
    // tenant — a separate cache entry from the tenant flags above, and a separate role.
    globalFlags: ["ops", "global-flags"],
    projectSettingsSchedules: ["project-settings", "schedules"],
    projectSettingsDisciplineMap: ["project-settings", "disciplineMap"],
    // Per-user settings (User.settings JSONB via /api/ai/user/settings)
    userSettings: (orgSlug: string, memberId: string) =>
        ["user-settings", orgSlug, memberId] as const,
    // ai_engine's base discipline catalog (`useDisciplineMap`) and the effective ranks.
    disciplineNames: ["discipline-names"],
    disciplineOrder: ["discipline-order"],
    disciplineUsage: ["discipline-usage"],
    /** Stytch organizations the member can switch into (ProjectSwitcher, OAuth consent). */
    discoveredOrganizations: (currentOrgId: string | undefined) =>
        ["discovered-organizations", currentOrgId] as const,
    destinationVisibility: ["destination-visibility"],
    /** The viewer's customized levels only (`?view=viewer`), a subset of the admin
     *  list above. Under its prefix so invalidating the list refreshes both. */
    destinationVisibilityViewer: ["destination-visibility", "viewer"],
    /** The shop-review contract set's document id (useContractDrawingDocumentId). */
    contractDrawingDocument: ["contract-drawing-document"],
    /** Tenant-wide destination names (Set Destination Name dialog's existing-
     *  destinations list). Tenant isolation is provided by TenantQueryClientProvider.
     *  See useDestinationNames and isDestinationDependentQueryKey. */
    destinationNames: ["destination-names"] as const,
    temporalDashboardUrl: ["temporal", "dashboard-url"],
    projectSettingsGridlines: ["project-settings", "gridlines"],
    projectSettingsGridlineKinks: ["project-settings", "gridline_kinks"],
    projectSettingsHomeAnnouncement: ["project-settings", "homeAnnouncement"],
    // Submittal reviews
    submittalReviews: "submittalReviews",
    submittalReview: (id: string) => ["submittalReview", id],
    submittalReviewStatus: (id: string) => ["submittalReviewStatus", id],
    submittalRegistry: (documentId: string) => ["submittal-registry", documentId],
    // The current-head register view. Revision-pinned views append the revision
    // id instead; both share the submittalRegistry prefix so prefix
    // invalidation reaches every view, but exact reads/writes (optimistic
    // updates) must target this key.
    submittalRegistryCurrent: (documentId: string) => ["submittal-registry", documentId, "current"],
    specSectionGroupLookup: (specDocumentId: string) => [
        "spec-section-group-lookup",
        specDocumentId,
    ],

    // Revision narrative
    revisionComparisons: REVISION_COMPARISONS_ROOT,
    /** Home's Revision Narrative row: the last three completed comparisons. */
    homeRevisionComparisons: [REVISION_COMPARISONS_ROOT, "home"] as const,
    revisionComparison: (id: string) => ["revision-comparison", id],
    revisionNarrativeExport: (
        comparisonId: string,
        format: NarrativeExportFormat = "overlay",
        runId: string | null = null,
    ) => ["revision-narrative-export", comparisonId, format, runId],
    revisionSheets: (id: string, params?: unknown) => ["revision-sheets", id, params],
    revisionSheet: (comparisonId: string, sheetId: string) => [
        "revision-sheet",
        comparisonId,
        sheetId,
    ],
    carryoverSheets: ["carryover-sheets"] as const,
    carryoverSheet: (sectionGroupId: string) => ["carryover-sheet", sectionGroupId] as const,
    revisionSummary: (id: string) => ["revision-summary", id],
    revisionReviewStatusStats: (id: string) => ["revision-review-status", id],
    revisionFacets: (id: string, filters?: unknown) => ["revision-facets", id, filters],

    sectionGroupVersions: (sgId: string) => ["section-group-versions", sgId] as const,
    sectionGroupVersionsRoot: ["section-group-versions"] as const,
    sectionGroup: (sectionGroupId: string) => ["section-group", sectionGroupId] as const,
    // Root prefixes for cross-cutting invalidation (refreshAfterAgentEdit):
    // spelled here so a key rename can't silently detach the invalidation.
    sectionGroupRoot: ["section-group"] as const,
    sectionGroupSectionsRoot: ["sectionGroup"] as const,
    textboxesRoot: ["textboxes"] as const,
    documentSheetsRoot: (documentId: string) => ["document", documentId, "sheets"] as const,
    // Spec division counts and one division's sections, under the sheets root so
    // every sheet-list invalidation refreshes them too.
    documentDivisions: (documentId: string, areaId?: string | null) =>
        ["document", documentId, "sheets", "divisions", areaId ?? null] as const,
    documentDivisionSections: (documentId: string, division: string, areaId?: string | null) =>
        ["document", documentId, "sheets", "division", division, areaId ?? null] as const,
    resolvedSheetForFile: (fileId: string) => ["resolved-sheet-for-file", fileId] as const,
    sectionGroupByFile: (
        documentId: string | null | undefined,
        fileId: string | null | undefined,
        sectionGroupId?: string | null,
    ) =>
        [
            "section-group-by-file",
            documentId,
            fileId,
            ...(sectionGroupId ? [sectionGroupId] : []),
        ] as const,

    taglistDestinationSuggestions: (taglistId: string, revisionId?: string) =>
        ["taglist", taglistId, "destination-suggestions", revisionId ?? null] as const,
    bulkClearByShapeDryRun: (
        fileId: string,
        page: number,
        revisionId: string | undefined,
        shapeId: number,
        scope: "sheet" | "discipline",
    ) => ["bulk-clear-by-shape", fileId, page, revisionId ?? null, shapeId, scope] as const,

    // Project-revisions ingest flow (api/revisions.ts). Names are deliberately
    // prefixed ``ingestRevision*`` to disambiguate from the unrelated revision
    // narrative/compare keys above (revisionComparisons, revisionSummary, …).
    ingestRevisionsAll: ["revisions", "all"] as const,
    // Shop-drawing package revisions (ShopDrawingsTable's in-flight progress
    // rows). Own root key so an upload can invalidate exactly this query
    // without touching ingestRevisionsAll's other consumers.
    shopDrawingRevisions: ["shop-drawing-revisions"] as const,
    // sectionGroupId scopes to the viewed copy's flags (deduped files are
    // shared across packages); part of the key so scoped/unscoped don't mix.
    shopSheetFlags: (fileId: string, sectionGroupId?: string) => [
        "shop-sheet-flags",
        fileId,
        ...(sectionGroupId ? [sectionGroupId] : []),
    ],
    /** Prefixes for cross-surface invalidation after a dismiss/reinstate. */
    shopSheetFlagsPrefix: ["shop-sheet-flags"],
    shopRevisionFlagsPrefix: ["shop-revision-flags"],
    /** One shop package's sheets for the review picker's package groups. */
    packageSheets: (documentId: string) => ["package-sheets", documentId],
    /** Ranked overlay candidates for a base sheet, scoped like the picker's other lists. */
    bestMatchSheets: (baseSheetFileId: string | null, areaId?: string) => [
        "best-match-sheets",
        baseSheetFileId,
        areaId ?? null,
    ],
    shopRevisionFlags: (revisionId: string) => ["shop-revision-flags", revisionId],
    shopDrawingSuggestions: (sectionGroupId: string) =>
        ["shop-drawing-suggestions", sectionGroupId] as const,
    // Baseline-picker options (RevisionsApi.listAll → RevisionOption[]). Kept on
    // its own key so it never shares a cache entry — and thus a queryFn — with
    // ingestRevisionsAll, which the revisions list owns with a different shape.
    ingestRevisionOptions: ["revisions", "all", "options"] as const,
    ingestRevisionsByDoc: (documentId: string) => ["revisions", documentId] as const,
    // Viewer revision picker: RevisionsApi.listChoices, a narrower shape than
    // ingestRevisionsByDoc's, so it keeps its own entry under that prefix
    // (invalidating a document's revisions refreshes both).
    revisionChoicesByDoc: (documentId: string) => ["revisions", documentId, "choices"] as const,
    // "All drawings impacted": RevisionsApi.impactedSheets for one sorted
    // selection. Under the "revisions" prefix so revision-list invalidations
    // refresh it too.
    revisionImpactedSheets: (revisionIds: readonly string[]) =>
        ["revisions", "impacted-sheets", ...revisionIds] as const,
    // Drawings > Revisions tab: RevisionsApi.listByDocumentType("drawing"). Keyed
    // by type, not document id, so an in-flight first upload (no sheets, hence
    // no canonical drawing document yet) still lists.
    ingestRevisionsByDocType: (documentType: string) =>
        ["revisions", "by-type", documentType] as const,
    // Admin > Revisions: RevisionsApi.list() called with no document_id, i.e.
    // every revision across every document. Distinct from ingestRevisionsAll
    // (RevisionsApi.listAll's lightweight RevisionOption[] baseline picker).
    adminAllRevisions: ["revisions", "admin-all"] as const,
    // Admin > Revisions (query-by-state redesign): NEEDS YOU / RUNNING merge a
    // revisions-scope query (setless work: manual uploads, spec revisions) with
    // a processing-sets-scope query (provider updates, manual replaces) --
    // never recency-capped, unlike adminAllRevisions above.
    adminRevisionsNeeds: ["revisions", "admin-needs"] as const,
    adminRevisionsActive: ["revisions", "admin-active"] as const,
    adminRevisionsHistory: (search: string, offset: number) =>
        ["revisions", "admin-history", search, offset] as const,
    adminProcessingSetsQueue: ["revisions", "processing-sets", "queue"] as const,
    // WAITING FOR IMPORT: deltas no import has claimed. Kept out of the
    // active key so an "Import latest" click refreshes both lists as the set
    // moves from one to the other.
    adminProcessingSetsDiscovered: ["revisions", "processing-sets", "discovered"] as const,
    adminAutoImport: ["revisions", "auto-import"] as const,
    revisionQualityReports: ["revision-quality", "reports"] as const,
    revisionQualityReport: (reportId: string) => ["revision-quality", "reports", reportId] as const,
    // Keyed by the report's status too: a pipeline report's sheet rows carry
    // pending/null connection values while it is partial, and the report id
    // does not change when it completes in place.
    revisionQualitySheets: (
        reportId: string,
        status: string,
        page: number,
        side?: string,
        sort?: string,
        direction?: string,
    ) =>
        [
            "revision-quality",
            "reports",
            reportId,
            "sheets",
            status,
            page,
            side,
            sort,
            direction,
        ] as const,
    // Keyed identically to the sheets page it's derived from — one batched
    // fetch per gallery page instead of one per row/card.
    revisionQualityBoxesBatch: (reportId: string, page: number, side?: string) =>
        ["revision-quality", "boxes-batch", reportId, page, side] as const,
    ingestRevision: (revisionId: string) => ["revisions", "detail", revisionId] as const,
    /** The viewer's polled connection-work verdict (api/revisions.ts
     *  connectionReview). Nested under ingestRevision on purpose: every gate
     *  approval and revision-stream event invalidates that prefix, so the
     *  verdict refreshes with them instead of waiting for its next poll. */
    ingestRevisionConnectionReview: (revisionId: string) =>
        ["revisions", "detail", revisionId, "connection-review"] as const,
    // Prefix of every ingestRevision detail — for mutations that change
    // sheet fields embedded in cached revision details (e.g. discipline).
    ingestRevisionDetailsRoot: ["revisions", "detail"] as const,
    ingestRevisionWorkflow: (revisionId: string) => ["revisions", "workflow", revisionId] as const,
    /** Self-serve upload wizard sheets payload (api/revisionWizard.ts, design §6.2). */
    wizardSheets: (revisionId: string) => ["revisions", "wizard-sheets", revisionId] as const,
    /** Every wizard sheets payload: a page move or a version-set date edit
     *  changes rows and picker options across uploads of one document. */
    wizardSheetsRoot: ["revisions", "wizard-sheets"] as const,
    /** The wizard's Discard: an idempotent POST the server may answer with
     *  `discarding`, polled on refetchInterval until it settles. */
    wizardDiscard: (revisionId: string) => ["revisions", "wizard-discard", revisionId] as const,
    /** Narrow bulk-ingest state, polled while a re-defer is being picked up. */
    wizardIngest: (revisionId: string) => ["revisions", "wizard-ingest", revisionId] as const,
    /** Wizard-owned revision detail (new/updated split, spec sections). Kept
     *  OFF the shared ingestRevision key: the SSE handler invalidates that
     *  one per node frame, which would refetch the full detail continuously
     *  while the wizard streams. */
    wizardRevisionSummary: (revisionId: string) =>
        ["revisions", "wizard-summary", revisionId] as const,
    /** Workflow node list polled by the spec wizard's Sections step to know when
     *  the parse node itself has settled (the revision rollup never does while
     *  the human publish gate is pending). */
    wizardRevisionWorkflow: (revisionId: string) =>
        ["revisions", "wizard-workflow", revisionId] as const,
    // areaId scopes the key like the request it caches; null is a real scope (a
    // sheet with no area), while omitting it yields the invalidation prefix.
    ingestRevisionSheetHistory: (documentId: string, groupCode: string, areaId?: string | null) =>
        areaId === undefined
            ? ["revisions", "sheet-history", documentId, groupCode]
            : ["revisions", "sheet-history", documentId, groupCode, areaId],
    ingestRevisionTableSections: (revisionId: string) =>
        ["revisions", "table-sections", revisionId] as const,
    /** Publish-notification recipient list (Revisions panel). */
    revisionNotificationSubscribers: ["revisions", "notification-subscribers"] as const,

    // File comparison (PP-1002)
    // Root prefix — invalidate this to cover every list/detail/items variant below.
    fileComparisonsRoot: ["file-comparisons"] as const,
    fileComparisons: (limit?: number, offset?: number) =>
        [...PrimeQueryKeys.fileComparisonsRoot, limit ?? null, offset ?? null] as const,
    fileComparison: (id: string) => [...PrimeQueryKeys.fileComparisonsRoot, id] as const,
    fileComparisonItems: (id: string, verdict?: string, limit?: number, offset?: number) =>
        [
            ...PrimeQueryKeys.fileComparisonsRoot,
            id,
            "items",
            verdict ?? "all",
            limit ?? null,
            offset ?? 0,
        ] as const,
    fileComparisonAllItems: (id: string) =>
        [...PrimeQueryKeys.fileComparisonsRoot, id, "items", "all-pages"] as const,
    fileComparisonItem: (id: string, itemId: string) =>
        [...PrimeQueryKeys.fileComparisonsRoot, id, "items", itemId] as const,

    // HBW Quality Dashboard (PP-1077)
    // Root prefix — an upload invalidates this to cover the version list and
    // every cached version at once.
    qualityDashboardRoot: ["quality-dashboard"] as const,
    qualityDashboardSnapshots: () => [...PrimeQueryKeys.qualityDashboardRoot, "snapshots"] as const,
    // `null` is the "whichever version is newest" request, a distinct cache
    // entry from any specific id: after an upload the newest changes, and
    // keying both under one entry would serve the previous version.
    qualityDashboardSnapshot: (snapshotId: string | null) =>
        [...PrimeQueryKeys.qualityDashboardRoot, "snapshot", snapshotId ?? "latest"] as const,
    // Tenant-level, not per-version: outside qualityDashboardRoot so an upload,
    // which invalidates that root, does not refetch them.
    qualityDashboardBranding: () => ["quality-dashboard-tenant", "branding"] as const,
    qualityDashboardDocumentLinks: () => ["quality-dashboard-tenant", "document-links"] as const,

    // Related Items v3 + Taxonomy Console (PP-863)
    relatedItemsV3: (anchorKey: string) => ["related-items-v3", anchorKey] as const,
    /** A drawing detail's checked related cards (the related-items detail check prototype). */
    relatedDetailCheck: (sectionUuid: string) => ["related-detail-check", sectionUuid] as const,
    /** A sheet whose details' checks were already asked for this session (the prefetch). */
    relatedDetailCheckPrefetch: (documentId: string, groupCode: string) =>
        ["related-detail-check", "prefetch", documentId, groupCode] as const,
    /** Resolved preview node for a related-item card — caches the sectionUuid
     *  (direct) or documentId+groupCode (sheet-revisions -> root-section) resolution
     *  chain so repeated cards for the same sheet/chapter share one round trip. */
    relatedItemPreviewNode: (
        sectionUuid: string | null | undefined,
        documentId: string | null | undefined,
        groupCode: string | null | undefined,
        sectionGroupId?: string | null,
    ) =>
        [
            "related-item-preview-node",
            sectionUuid ?? null,
            documentId ?? null,
            groupCode ?? null,
            sectionGroupId ?? null,
        ] as const,
    /** The staff exclusion review: unconfirmed exclusion categories, a page at a time. */
    connectionExclusionsRoot: () => ["connection-exclusions"] as const,
    connectionExclusionCategories: (offset: number) =>
        ["connection-exclusions", "categories", offset] as const,
    taxonomyRollout: ["taxonomy", "rollout"] as const,
    taxonomyTree: ["taxonomy", "tree"] as const,
    taxonomyNodeEntities: (taxonomyId: number, limit: number) =>
        ["taxonomy", "node-entities", taxonomyId, limit] as const,
    taxonomyAssignments: (
        entityKind: string,
        entityId?: string,
        documentId?: string,
        groupCode?: string,
        hId?: string,
    ) =>
        [
            "taxonomy",
            "assignments",
            entityKind,
            entityId ?? null,
            documentId ?? null,
            groupCode ?? null,
            hId ?? null,
        ] as const,
    /** Every workbench and Marvin Explore key, all projects and callers: the revision
     *  lifecycle invalidates it, and `shouldPersistQuery` keeps it out of browser storage. */
    workbenchRoot: ["workbench"] as const,
    /** One caller's workbench reads in one project (`useWorkbenchQueryScope`). The
     *  answers are filtered for the caller, so the caller is part of every key. */
    workbenchScope: (project: string, caller: string) => ["workbench", project, caller] as const,
    /** One assembled workbench, keyed by the seed the canvas was built around.
     *  `graphVersion` is the revision dimension: pass the project graph's version when
     *  the caller holds it, so a publish or connection edit misses the old bench. */
    workbench: (
        project: string,
        caller: string,
        seedKind: string,
        seedId: string | null,
        graphVersion: string | null = null,
    ) => ["workbench", project, caller, "assemble", seedKind, seedId, graphVersion] as const,
    /** Tier 1, the whole project graph. No version in the key: the version is in the
     *  answer, and a refetch revalidates the held copy with `If-None-Match`. */
    workbenchGraph: (project: string, caller: string) =>
        ["workbench", project, caller, "graph"] as const,
    /** The grid space buildings. Versioned like the graph: the held copy revalidates. */
    workbenchSpace: (project: string, caller: string) =>
        ["workbench", project, caller, "space"] as const,
    /** How one space was built (admins); a new build moves the space's version, so it rides along. */
    workbenchSpaceTrace: (
        project: string,
        caller: string,
        document: string,
        area: string,
        spaceVersion: string | null,
    ) => ["workbench", project, caller, "spaceTrace", document, area, spaceVersion] as const,
    /** Tier 2 for one sheet set (pass `normalizeSheetIds` output) under one graph version. */
    workbenchBlocks: (
        project: string,
        caller: string,
        graphVersion: string | null,
        sheetIds: readonly string[],
    ) => ["workbench", project, caller, "blocks", graphVersion, sheetIds.join(",")] as const,
    /** E1, one sheet. The route has no ETag, so the graph version moves the key. */
    workbenchSheet: (
        project: string,
        caller: string,
        sheetId: string | null,
        graphVersion: string | null,
    ) => ["workbench", project, caller, "sheet", sheetId, graphVersion] as const,
    /** Where the caller left Explore (`/user/explore-session`). */
    workbenchExploreSession: (project: string, caller: string) =>
        ["workbench", project, caller, "explore-session"] as const,
    /** The batched crop prefetch for one bench (`benchCrops.ts`). */
    workbenchCrops: (benchKey: string) => ["workbench-crops", benchKey] as const,
};

/**
 * First query-key segment for every document-area query eligible for warm-reload
 * persistence when the local-cache opt-in cookie is absent (default persistence).
 * Keeps default IndexedDB writes scoped to the surfaces that benefit from an
 * instant paint on reload — document library/folders, spec & drawing document
 * lists, and the RFI dashboard — instead of silently growing to cover the whole
 * app cache. The opt-in cookie (`prime_enable_local_cache`) bypasses this
 * allowlist and persists everything, as before.
 *
 * `documentSectionGroups` is deliberately excluded: it holds every section
 * group for a whole document type (drawing or csi) in one query and can run
 * multi-MB, which made every throttled persist tick re-stringify a multi-MB
 * payload on the main thread (see `TenantQueryClientProvider`'s `THROTTLE_TIME`).
 */
export const DOCUMENT_AREA_QUERY_KEY_ROOTS = [
    AI_DOCUMENTS_ROOT,
    DOCUMENTS_ROOT,
    DOCUMENT_SUMMARY_ROOT,
    FOLDERS_ROOT,
    DOCUMENTS_FOLDERED_ROOT,
    RFIS_ROOT,
] as const;

export const DRAWING_SHEET_PAGE_SIZE = 250;

/** Ledger (compact desktop) page size for the Specifications table's division groups. */
export const SPEC_SECTION_PAGE_SIZE = 250;

/** API sentinel for the unassigned-area bucket (matches ai_engine's `?area_id=unassigned`). */
export const AREA_UNASSIGNED = "unassigned";

/** Route of the Manage Areas page. FE-1 registers it; all other code navigates via this. */
/**
 * Where an item's attachments open in the editor (`:recordId` is the item). A neutral module so the
 * router, the tablet registry and `utils/navigation` read one address without importing a feature.
 */
export const ATTACHMENT_EDITOR_ROUTE = "/documents/attachments/:recordId";

export const MANAGE_AREAS_PATH = "/documents/areas";

/** Route of the Transmittals workspace: a top-level Documents
 *  sidebar entry, not a per-register tab. */
export const TRANSMITTALS_PATH = "/documents/transmittals";

/** The Drawings screen (`screens/Drawings`): where every Drawings link goes, in classic and
 *  Design v2 alike. */
export const DRAWINGS_PATH = "/drawings";

// Self-serve upload wizard (PP-836). The wizard renders inside the Documents
// chrome (header + category sidebar via SheetsPageShell); ProjectDocuments
// navigates here for flag-on tenants.
export const DRAWINGS_UPLOAD_PATH = "/drawings/upload";
export const SPECS_UPLOAD_PATH = "/specs/upload";

/** Compliance-critical legal pages (Autodesk listing requires an in-app link); keep every in-app reference on these constants. */
export const PRIVACY_POLICY_URL = "https://www.primepoint.ai/legal/privacy-policy";
export const TERMS_OF_SERVICE_URL = "https://www.primepoint.ai/legal/terms";

// Externally managed docs routes. ExternalDocsShell, App.tsx, the web shell's admin menu and
// AdminOverview.tsx all navigate via these so the paths live in one place.
// Revisions (the dual-pane import/link view) is the default view; Setup holds the
// mapping model, and Reports survives as the "Sync history" tab.
/** The carry-over review tool (`components/carryover`): an inspect tool for what the seam did to a
 *  sheet, read by whoever is debugging the pipeline, so under `/admin` rather than `/w`. */
export const CARRYOVER_ROOT = "/admin/carryover";
export const EXTERNAL_DOCS_ROOT_PATH = "/admin/external-docs";
export const EXTERNAL_DOCS_SETUP_PATH = "/admin/external-docs/setup";
export const EXTERNAL_DOCS_REVISIONS_PATH = "/admin/external-docs/revisions";
export const EXTERNAL_DOCS_BROWSER_PATH = "/admin/external-docs/browser";
export const EXTERNAL_DOCS_REPORTS_PATH = "/admin/external-docs/reports";
/** Old bookmarked route — App.tsx redirects it to `EXTERNAL_DOCS_BROWSER_PATH`. */
export const LEGACY_EXTERNAL_FILES_PATH = "/admin/external-files";

export const REVISION_DASH_ROOT_PATH = "/admin/revisionDash";
/** Usage, engagement across every project, for Primepoint staff (`components/usage`). */
export const USAGE_ROOT_PATH = "/usage";
/** The Engagement reports page, beside Usage; its old `/admin/engagement` address lands on Usage. */
export const ENGAGEMENT_REPORTS_PATH = "/admin/engagement-reports";
/** Usage's printable report, drawn without the app's chrome (`navigation/appChrome.ts`). */
export const USAGE_REPORT_PATH = "/usage/report";
/** Project data status' New project opens the Admin console's (PP-1231). */
export const ADMIN_CONSOLE_NEW_PROJECT_PATH = "/admin/console/projects/new";
export const MIRANDA_RUNS_ROOT_PATH = "/admin/mirandaRuns";
export const PRECON_ONTOLOGY_ROOT_PATH = "/admin/ontology";
export const MARVIN_EVAL_RUNS_ROOT_PATH = "/admin/marvin-eval-runs";
export const MARVIN_SETTINGS_ROOT_PATH = "/admin/marvin-settings";

/** Documents Library route (`/documents/:category` with the "documents" category) — the
 *  synced-but-not-imported file browser reached from search results and the sidebar. */
export const DOCUMENTS_LIBRARY_PATH = "/documents/documents";

/**
 * Known project setting keys for type safety.
 * Add new setting keys here to get compile-time validation.
 */
export const ProjectSettingKeys = {
    disciplineMap: "disciplineMap",
    gridlines: "gridlines",
    homeAnnouncement: "project.home.announcement",
} as const;

export type ProjectSettingKey = (typeof ProjectSettingKeys)[keyof typeof ProjectSettingKeys];

// Marvin AI assistant user ID - used for checking if a message is from Marvin
export const MARVIN_USER_ID = "ef15fdb1-586b-439e-8d5a-c733eb1acb9c";

export const THINKING_MESSAGES = [
    "Measuring twice...",
    "Cross-referencing...",
    "Tracing...",
    "Reviewing...",
    "Thinking...",
    "Contemplating...",
    "Analyzing...",
    "Investigating...",
    "Examining...",
    "Deliberating...",
];
